import { formatCalendarDateForInput } from "./calendarDate.mjs";

const isTrue = (value) => value === true || value === "true";

export function courtTimeForInput(value) {
  if (typeof value !== "string") return "";
  const match = value.match(/^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/);
  return match ? `${match[1]}:${match[2]}` : "";
}

export function formatCourtTime(value, uppercase = false) {
  const clock = courtTimeForInput(value);
  if (!clock) return "";
  const [hours, minutes] = clock.split(":");
  const hour = Number(hours);
  const suffix = hour >= 12 ? "pm" : "am";
  return `${hour % 12 || 12}:${minutes} ${uppercase ? suffix.toUpperCase() : suffix}`;
}

export function formatCourtDate(value, style = "panel") {
  const day = formatCalendarDateForInput(value);
  if (!day) return "";
  const [year, month, date] = day.split("-");
  if (style === "short") return `${Number(month)}/${Number(date)}/${year.slice(-2)}`;
  if (style === "calendar") return `${Number(month)}/${Number(date)}/${year}`;
  if (style === "month") {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${months[Number(month) - 1]} ${Number(date)}, ${year}`;
  }
  return `${Number(month)}/${date}/${year}`;
}

export function formatCourtDateTime(
  value,
  time,
  { dateStyle, separator = " ", uppercase = false } = {}
) {
  const day = formatCourtDate(value, dateStyle);
  if (!day) return "";
  const clock = formatCourtTime(time, uppercase);
  return clock ? `${day}${separator}${clock}` : day;
}

export function getActEndpoint(act) {
  if (isTrue(act?.is_duration) && act?.start_date) return "start_date";
  if (isTrue(act?.uses_end_date) && act?.end_date) return "end_date";
  return act?.start_date ? "start_date" : "end_date";
}

export function getActTime(act, field) {
  if (isTrue(act?.is_allday) || isTrue(act?.all_day)) return null;
  if (field === "start_date" || field === "start") return act?.start_time ?? null;
  if (field === "end_date" || field === "end") return act?.end_time ?? null;
  if (
    [
      "dependant_date",
      "service_date",
      "hearing_date",
      "trial_date",
      "motion_date",
      "exam_date",
      "deposition_date",
    ].includes(field)
  ) {
    return isTrue(act?.is_dependant_date) ? (act?.start_time ?? null) : (act?.end_time ?? null);
  }
  return null;
}

export const formatActDate = (act, field, options) =>
  formatCourtDateTime(act?.[field], getActTime(act, field), options);

export function courtCalendarStart(day, time) {
  const date = formatCalendarDateForInput(day);
  if (!date) return null;
  return courtTimeForInput(time) ? `${date}T${time}` : date;
}

// Calendar weekdays are arithmetic on the saved day, never browser-local instants.
export function courtWeekday(value) {
  const date = formatCalendarDateForInput(value);
  if (!date) return "";
  const [, month, day] = date.split("-").map(Number);
  let year = Number(date.slice(0, 4));
  const offsets = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];
  if (month < 3) year--;
  const index =
    (year +
      Math.floor(year / 4) -
      Math.floor(year / 100) +
      Math.floor(year / 400) +
      offsets[month - 1] +
      day) %
    7;
  return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][index];
}

export function actDatePayload(values) {
  const payload = { ...values };
  for (const key of [
    "event_timezone",
    "effective_timezone",
    "starts_at",
    "ends_at",
    "deadline_at",
  ]) {
    delete payload[key];
  }
  for (const key of [
    "start_date",
    "end_date",
    "dependant_date",
    "accepted_on",
    "rejected_on",
    "service_date",
    "hearing_date",
    "trial_date",
    "motion_date",
    "exam_date",
    "deposition_date",
  ]) {
    if (key in payload) payload[key] = formatCalendarDateForInput(payload[key]) || null;
  }
  for (const key of ["start_time", "end_time"]) {
    payload[key] = isTrue(payload.is_allday) ? null : courtTimeForInput(payload[key]) || null;
  }
  return payload;
}

export function actValidationMessage(error, fallback = "Unable to save the event") {
  const data = error?.response?.data;
  const messages = (value) => {
    if (typeof value === "string") return [value];
    if (Array.isArray(value)) return value.flatMap(messages);
    if (value && typeof value === "object") return Object.values(value).flatMap(messages);
    return [];
  };
  return messages(data?.error || data?.detail || data).join(" ") || fallback;
}
